import { useState } from "react"

import { useDeleteAIProvider } from "@/api/useTenant"
import { Button, Card, Flex, Skeleton, Tag, Text } from "@/atoms"
import { useTranslation } from "@/hooks/useTranslation"
import { CheckCircleOutlined, ExclamationCircleOutlined } from "@/icons"
import { DeleteConfirmModal, useConfirmModal } from "@/molecules/ConfirmModal"
import { Heading } from "@/molecules/Heading"
import { Protected } from "@/molecules/Protected"
import { getValidationText } from "@/pages/SettingsPage/helpers/aiProvider"
import { AIProviderModal } from "../AIProviderModal"

import styles from "./AIProviderCard.module.scss"

import type { AIValidationStatus } from "@/api/useTenant.types"
import type { TagProps } from "@/atoms"
import type { AIProviderCardProps } from "."

const STATUS_TAG: Record<
  AIValidationStatus,
  Pick<TagProps, "color" | "icon">
> = {
  not_configured: {},
  valid: { color: "success", icon: <CheckCircleOutlined /> },
  failed: { color: "error", icon: <ExclamationCircleOutlined /> }
}

export const AIProviderCard = (props: AIProviderCardProps) => {
  const { provider, isLoading, isError } = props
  const { t } = useTranslation("translation", { keyPrefix: "settings.kai" })
  const { t: tGeneral } = useTranslation()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const deleteProvider = useDeleteAIProvider()
  const { confirm } = useConfirmModal()
  const status = provider?.validation.status || "not_configured"
  const failure =
    provider && status === "failed"
      ? getValidationText(provider.validation, t)
      : undefined
  const vendor = provider?.vendor
    ? t(`aiProvider.vendors.${provider.vendor}`)
    : undefined
  const onRemove = () =>
    confirm((attr) =>
      DeleteConfirmModal({
        entityName: "ai-provider",
        title: t("aiProvider.remove.title"),
        content: t("aiProvider.remove.content"),
        okText: t("aiProvider.remove.ok"),
        messages: { success: t("aiProvider.remove.success") },
        onOk: () => deleteProvider.mutateAsync(),
        ...attr
      })
    )
  const location = provider?.vendor
    ? [provider.projectId, provider.region].filter(Boolean).join(" | ")
    : undefined

  return (
    <Card className={styles.card}>
      <Flex vertical gap={12}>
        <Flex vertical gap={4}>
          <Heading level={5} title={t("aiProvider.title")} />
          <Text type="secondary" className="sm-text-ks">
            {t("aiProvider.description")}
          </Text>
        </Flex>
        {isLoading && <Skeleton active paragraph={{ rows: 1 }} />}
        {isError && (
          <Text type="danger">{tGeneral("selfHosted.serverGeneralError")}</Text>
        )}
        {!isLoading && !isError && (
          <Flex vertical gap={8}>
            <Text>{t("aiProvider.providerCaption")}</Text>
            <Flex
              justify="space-between"
              align="center"
              gap={12}
              className={styles.callout}
            >
              <Flex vertical gap={2}>
                <Flex align="center" gap={8}>
                  <Text type={vendor ? undefined : "secondary"}>
                    {vendor || t("aiProvider.notConfigured")}
                  </Text>
                  {status !== "not_configured" && (
                    <Tag {...STATUS_TAG[status]}>
                      {t(`aiProvider.status.${status}`)}
                    </Tag>
                  )}
                </Flex>
                {location && (
                  <Text type="secondary" className="sm-text-ks">
                    {location}
                  </Text>
                )}
                {failure && (
                  <Flex vertical gap={2}>
                    <Text type="danger">{failure.title}</Text>
                    {failure.detail && (
                      <Text type="secondary" className="sm-text-ks">
                        {failure.detail}
                      </Text>
                    )}
                  </Flex>
                )}
              </Flex>
              <Flex gap={8}>
                {provider?.configured && (
                  <Protected
                    permission={{ resource: "system", action: "write" }}
                  >
                    <Button danger onClick={onRemove}>
                      {t("aiProvider.remove.ok")}
                    </Button>
                  </Protected>
                )}
                <Protected
                  shouldDisable
                  permission={{ resource: "system", action: "write" }}
                >
                  <Button onClick={() => setIsModalOpen(true)}>
                    {provider?.configured
                      ? tGeneral("general.edit")
                      : t("aiProvider.configure")}
                  </Button>
                </Protected>
              </Flex>
            </Flex>
          </Flex>
        )}
      </Flex>
      {isModalOpen && (
        <AIProviderModal
          provider={provider}
          onClose={() => setIsModalOpen(false)}
        />
      )}
    </Card>
  )
}
